import { useRestApiProvider } from "@jugl-web/rest-api";
import {
  TaskCustomFieldModel,
  transformTaskCustomFieldDtoToTaskCustomFieldModel,
  transformTaskCustomFieldModelToTaskCustomFieldDto,
} from "@jugl-web/rest-api/tasks/models/TaskCustomField";
import { useCallback, useMemo } from "react";
import { useTaskCustomFieldGetters } from "./useTaskCustomFieldGetters";

interface UseTaskCustomFieldsProps {
  entityId: string;
  /**
   * Null is supported only to make the API more convenient to use.
   * If `boardId` is `null`, no fields will be returned.
   */
  boardId: string | null;
}

type NewTaskCustomField = Pick<
  TaskCustomFieldModel,
  "name" | "type" | "isShownInCard" | "values"
>;

/**
 * Source of board custom fields (entity-level custom fields are not supported within that hook).
 * If `boardId` is not provided, no fields will be returned.
 */
export const useTaskCustomFields = ({
  entityId,
  boardId,
}: UseTaskCustomFieldsProps) => {
  const { getCustomFieldsByBoard, produceGetCustomFieldById } =
    useTaskCustomFieldGetters({ entityId });

  const { tasksApi } = useRestApiProvider();

  const boardCustomFields = useMemo<TaskCustomFieldModel[]>(
    () => (boardId ? getCustomFieldsByBoard(boardId) : []),
    [boardId, getCustomFieldsByBoard]
  );

  const getCustomFieldById = useMemo(() => {
    if (!boardId) {
      return () => undefined;
    }

    return produceGetCustomFieldById(boardId);
  }, [boardId, produceGetCustomFieldById]);

  // #region Mutations
  const [createTaskCustomFieldMutation, { isLoading: isCreatingCustomField }] =
    tasksApi.useCreateTaskCustomFieldMutation();

  const [updateTaskCustomFieldMutation, { isLoading: isUpdatingCustomField }] =
    tasksApi.useUpdateTaskCustomFieldMutation();

  const [deleteTaskCustomFieldMutation, { isLoading: isDeletingCustomField }] =
    tasksApi.useDeleteTaskCustomFieldMutation();

  const [updateTaskCustomFieldsInBulkMutation] =
    tasksApi.useUpdateTaskCustomFieldsInBulkMutation();

  const createCustomField = useCallback(
    (newCustomField: NewTaskCustomField) =>
      new Promise<TaskCustomFieldModel>((resolve, reject) => {
        if (!boardId) {
          reject(new Error("Board ID is required for creating custom field"));
          return;
        }

        createTaskCustomFieldMutation({
          entityId,
          boardId,
          customField: {
            field: newCustomField.name,
            field_type: newCustomField.type,
            show_in_card: newCustomField.isShownInCard,
            order: boardCustomFields.length + 1,
            values: newCustomField.values,
          },
          meta: { silent: true },
        }).then((response) => {
          if ("data" in response) {
            const updatedCustomField =
              transformTaskCustomFieldDtoToTaskCustomFieldModel(response.data);

            resolve(updatedCustomField);
          } else {
            reject(response.error);
          }
        });
      }),
    [boardId, createTaskCustomFieldMutation, entityId, boardCustomFields.length]
  );

  const updateCustomField = useCallback(
    (updatedCustomField: TaskCustomFieldModel, optimistic = false) =>
      new Promise<void>((resolve, reject) => {
        if (!boardId) {
          reject(new Error("Board ID is required for updating custom field"));
          return;
        }

        updateTaskCustomFieldMutation({
          entityId,
          boardId,
          updatedCustomField:
            transformTaskCustomFieldModelToTaskCustomFieldDto(
              updatedCustomField
            ),
          optimistic,
        }).then((response) => {
          if ("data" in response) {
            resolve();
          } else {
            reject(response.error);
          }
        });
      }),
    [boardId, entityId, updateTaskCustomFieldMutation]
  );

  const updateCustomFieldsInBulk = useCallback(
    (updatedCustomFields: TaskCustomFieldModel[]) =>
      new Promise<void>((resolve, reject) => {
        if (!boardId) {
          reject(
            new Error("Board ID is required for reordering custom fields")
          );
          return;
        }

        updateTaskCustomFieldsInBulkMutation({
          entityId,
          boardId,
          customFields: updatedCustomFields.map(
            transformTaskCustomFieldModelToTaskCustomFieldDto
          ),
        }).then((response) => {
          if ("data" in response) {
            resolve();
          } else {
            reject(new Error("Failed to reorder custom fields"));
          }
        });
      }),
    [boardId, entityId, updateTaskCustomFieldsInBulkMutation]
  );

  const deleteCustomField = useCallback(
    (customFieldId: string) =>
      new Promise<void>((resolve, reject) => {
        if (!boardId) {
          reject(new Error("Board ID is required for deleting custom field"));
          return;
        }

        deleteTaskCustomFieldMutation({
          entityId,
          boardId,
          customFieldId,
        }).then((response) => {
          if ("data" in response) {
            resolve();
          } else {
            reject(new Error("Failed to delete custom field"));
          }
        });
      }),
    [boardId, deleteTaskCustomFieldMutation, entityId]
  );
  // #endregion

  return {
    boardCustomFields,
    getCustomFieldById,

    // Mutations
    createCustomField,
    updateCustomField,
    deleteCustomField,
    updateCustomFieldsInBulk,
    isCreatingCustomField,
    isUpdatingCustomField,
    isDeletingCustomField,
  };
};
